import { createFileRoute } from "@tanstack/react-router";
import { formatDateOnly, errorWords } from "@alm/domain";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AmyMoveInAction } from "@/components/amy/amy-move-in-action";
import { EmptyState } from "@/components/app/lists/empty-state";
import { MoveInBoard } from "@/components/app/clinical/move-in-board";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { useZone } from "@/lib/facility/zone";
import { moveInStepLink } from "./-components/stay/move-in-step-action";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/move-in")({
  component: MoveInTab,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** One resident's board: every step, its evidence, and its deadline. */
function MoveInTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canWrite = useCan("resident.profile:update", resident.facilityId);
  const zone = useZone(resident.facilityId);
  const queryClient = useQueryClient();

  const board = useQuery(orpc.care.moveIn.forResident.queryOptions({ input: { residentId } }));
  const setStep = useMutation(
    orpc.care.moveIn.setStep.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.care.moveIn.key() });
      },
      onError: fail,
    }),
  );

  if (board.isPending) return <Skeleton className="h-96 w-full" />;
  if (board.isError)
    return (
      <ReadFailed
        message="Could not load the move-in board."
        error={board.error}
        onRetry={() => void board.refetch()}
        retrying={board.isFetching}
      />
    );
  if (!board.data) return <EmptyState title="No board" description="Nothing to show yet." />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <AmyMoveInAction
          facilityId={resident.facilityId}
          target={{
            kind: "resident",
            id: residentId,
            name: `${resident.firstName} ${resident.lastName}`,
          }}
        />
      </div>
      <MoveInBoard
        board={board.data}
        canWrite={canWrite}
        pending={setStep.isPending}
        dateNote={`Admission ${formatDateOnly(board.data.admissionDate, zone)}`}
        facilityId={resident.facilityId}
        /* The only thing a resident's board has that a lead's cannot: the chart
         tab that satisfies the step. */
        stepLink={(step) => moveInStepLink(residentId, step)}
        onSetStep={(input) => setStep.mutate({ residentId, ...input })}
      />
    </div>
  );
}
